import {useEffect} from 'react';
import {TraceTokEvents} from '@src/Tracking/catalog';
import {useTrackingContext} from '@src/Providers/Tracking/TrackingContext';

/**
 * P2: error_encountered — global uncaught error + unhandledrejection
 * listeners. Emits a bucketed error event so we don't leak PII or stack
 * traces to analytics.
 */
const bucketCode = (msg: string): string => {
  const m = msg.toLowerCase();
  if (m.includes('network')) return 'network';
  if (m.includes('timeout')) return 'timeout';
  if (m.includes('chunk')) return 'chunk_load';
  if (m.includes('auth') || m.includes('unauthor')) return 'auth';
  if (m.includes('player') || m.includes('hls') || m.includes('video'))
    return 'player';
  return 'js_runtime';
};

export const useGlobalErrorTracking = () => {
  const {emit} = useTrackingContext();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onError = (e: ErrorEvent) => {
      emit(TraceTokEvents.ErrorEncountered, {
        error_code_bucket: bucketCode(e.message || ''),
        error_surface: 'window.error',
      });
    };
    const onUnhandled = (e: PromiseRejectionEvent) => {
      const reason = String(e.reason?.message || e.reason || '');
      emit(TraceTokEvents.ErrorEncountered, {
        error_code_bucket: bucketCode(reason),
        error_surface: 'unhandledrejection',
      });
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandled);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandled);
    };
  }, [emit]);
};

export default useGlobalErrorTracking;
